import React, { useState, useEffect } from 'react';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';

interface VoteButtonsProps {
  docId: string;
  initialUpvotes?: number;
  initialDownvotes?: number;
}

const VoteButtons: React.FC<VoteButtonsProps> = ({
  docId,
  initialUpvotes = 0,
  initialDownvotes = 0,
}) => {
  const [upvotes, setUpvotes] = useState(initialUpvotes);
  const [downvotes, setDownvotes] = useState(initialDownvotes);
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const API_URL = import.meta.env.VITE_API_BACKEND_URL;

  // Load user's vote from localStorage
  useEffect(() => {
    const storedVote = localStorage.getItem(`doc_vote_${docId}`);
    if (storedVote === 'up' || storedVote === 'down') {
      setUserVote(storedVote);
    }
  }, [docId]);

  const handleVote = async (voteType: 'up' | 'down') => {
    // Don't allow voting if already processing or if user already voted the same way
    if (userVote === voteType) {
      // User clicking the same vote again - ignore for simplicity
      return;
    }

    // Optimistic update
    const previousVote = userVote;
    const previousUpvotes = upvotes;
    const previousDownvotes = downvotes;

    // Update UI immediately
    if (voteType === 'up') {
      setUpvotes(upvotes + 1);
      if (previousVote === 'down') {
        setDownvotes(downvotes - 1);
      }
    } else {
      setDownvotes(downvotes + 1);
      if (previousVote === 'up') {
        setUpvotes(upvotes - 1);
      }
    }

    setUserVote(voteType);
    localStorage.setItem(`doc_vote_${docId}`, voteType);

    // Send vote to backend API
    try {
      const response = await fetch(`${API_URL}/documentation/dev-docs/${docId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote: voteType }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit vote');
      }

      const data = await response.json();
      // Update with actual counts from server
      setUpvotes(data.upvotes);
      setDownvotes(data.downvotes);
    } catch (err) {
      console.error('Failed to submit vote:', err);
      // Revert on error
      setUpvotes(previousUpvotes);
      setDownvotes(previousDownvotes);
      setUserVote(previousVote);
      if (previousVote) {
        localStorage.setItem(`doc_vote_${docId}`, previousVote);
      } else {
        localStorage.removeItem(`doc_vote_${docId}`);
      }
    }
  };

  return (
    <div className="flex items-center gap-4">
      {/* Upvote */}
      <button
        onClick={() => handleVote('up')}
        className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 ${
          userVote === 'up'
            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg scale-105'
            : 'backdrop-blur-xl bg-white/60 border border-white/40 text-gray-700 hover:bg-green-50 hover:border-green-300 hover:scale-105'
        }`}
        title="Helpful"
      >
        <FaThumbsUp className={userVote === 'up' ? 'text-white' : 'text-green-600'} />
        <span className="font-bold">{upvotes}</span>
      </button>

      {/* Downvote */}
      <button
        onClick={() => handleVote('down')}
        className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all duration-300 ${
          userVote === 'down'
            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg scale-105'
            : 'backdrop-blur-xl bg-white/60 border border-white/40 text-gray-700 hover:bg-red-50 hover:border-red-300 hover:scale-105'
        }`}
        title="Not helpful"
      >
        <FaThumbsDown className={userVote === 'down' ? 'text-white' : 'text-red-600'} />
        <span className="font-bold">{downvotes}</span>
      </button>
    </div>
  );
};

export default VoteButtons;
